import { CashInfo, Currency, getFormatter, useCurrencyExchangeRateRT } from '@eih-netbroker/common';
import { ReactComponent as AlertIcon } from '../../../../assets/icons/alert-triangle.svg';
import { ReactComponent as CurrencyExchangeIcon } from '../../../../assets/icons/exchange-loop.svg';
import { ReactComponent as NoCurrencyExchangeIcon } from '../../../../assets/icons/none.svg';
import { NbButton } from '../../../../shared/components/button/Button';
import { NbTooltip } from '../../../../shared/components/tooltip/Tooltip';
import { cssWhen } from '../../../../shared/utils/themeUtils';

import { AccountCategory } from '@eih-netbroker/common';
import { BaseCurrency } from '@eih-netbroker/common/dist/utils/instrumentUtils';
import { ReactElement, useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { CurrencyFlag } from '../../../../shared/components/currency-flag/CurrencyFlag';
import { MoneyMaskHOC } from '../../../../shared/components/money-mask/MoneyMask';
import { Config } from '../../../../shared/config/config';
import { useAccounts } from '../../../../shared/services/useAccounts';
import classes from './CashPopoverListItem.module.scss';

export type FiatWithExchangePeriodInfo = CashInfo & {
  isExchangePeriod: 'loading' | 'active' | 'disabled' | 'unavailable';
};

export interface CashPopoverItemProps {
  fiatWithExchangePeriodInfo: FiatWithExchangePeriodInfo;
  onClick?: (currency: string) => void;
}

export const CashPopoverListItem = ({ fiatWithExchangePeriodInfo: fiat, onClick }: CashPopoverItemProps) => {
  const { translation } = useLocale();
  const { formatCurrency } = getFormatter();
  const { selectedAccountOrClient } = useAccounts();
  const { currenciesExchangeRateRT } = useCurrencyExchangeRateRT();

  const fiatInHuf = useMemo(() => {
    if (fiat.ccy === Config.NetBroker.BaseCurrency) {
      return fiat.inventory;
    }
    const rate = currenciesExchangeRateRT[`${fiat.ccy as Currency}${BaseCurrency}=`];
    if (rate && rate.isLoaded) {
      return fiat.inventory * rate.rateInHuf;
    }
    return fiat.inventoryHuf;
  }, [fiat, currenciesExchangeRateRT]);

  const withTooltip = (isUnavailable: boolean, node: ReactElement) => {
    if (isUnavailable) {
      return (
        <NbTooltip
          title={translation.currencyChange.note.unavailable}
          placement={'top'}
          children={node}
        />
      );
    }
    return node;
  };

  return (
    <div className={classes.listItem}>
      <div className={classes.listItemHeader}>
        <div className={classes.listItemHeaderLeft}>
          <CurrencyFlag currency={fiat.ccy} />
          <p>{fiat.ccy}</p>
        </div>
        <MoneyMaskHOC
          isInverse
          size={100}
        >
          <p
            className={classes.listItemIsBold}
            data-id={`header-cash-fiat-${fiat.ccy}`}
          >
            {formatCurrency(fiat.inventory, fiat.ccy)}
          </p>
        </MoneyMaskHOC>
      </div>
      <div className={classes.listItemContent}>
        <div className={classes.listItemContentLeft}>
          {selectedAccountOrClient.account &&
            selectedAccountOrClient.account.accountCategory !== AccountCategory.Nyesz &&
            withTooltip(
              fiat.isExchangePeriod === 'unavailable',
              <NbButton
                size={'small'}
                variant={'outlined'}
                className={cssWhen(
                  classes.listItemExchangeButton,
                  classes.listItemExchangeButtonIsSmall,
                  [fiat.isExchangePeriod === 'disabled', classes.listItemExchangeButtonIsDisabled],
                  [fiat.isExchangePeriod === 'loading', classes.listItemExchangeButtonIsDisabled],
                  [fiat.isExchangePeriod === 'unavailable', classes.listItemExchangeButtonIsUnavailable],
                )}
                onClick={() => {
                  if (fiat.isExchangePeriod !== 'unavailable') {
                    onClick?.(fiat.ccy);
                  }
                }}
              >
                {fiat.isExchangePeriod === 'unavailable' && <NoCurrencyExchangeIcon />}
                {fiat.isExchangePeriod !== 'unavailable' && <CurrencyExchangeIcon />}
                {fiat.ccy}
              </NbButton>,
            )}
        </div>
        <div className={classes.listItemContentRight}>
          {fiat.allocated > 0 && (
            <MoneyMaskHOC
              isInverse
              size={60}
            >
              <p
                className={classes.listItemContentRightAllocated}
                data-id={`header-cash-fiat-allocated-${fiat.ccy}`}
              >
                <NbTooltip
                  title={translation.header.cash.allocatedTooltip}
                  placement={'top'}
                >
                  <AlertIcon />
                </NbTooltip>
                {formatCurrency(fiat.allocated, fiat.ccy)}
              </p>
            </MoneyMaskHOC>
          )}
          {selectedAccountOrClient.account?.accountCategory !== AccountCategory.Nyesz && (
            <MoneyMaskHOC
              isInverse
              size={100}
            >
              <p
                className={classes.listItemLight}
                data-id={`header-cash-fiat-${fiat.ccy}-huf`}
              >
                {formatCurrency(fiatInHuf, 'HUF')}
              </p>
            </MoneyMaskHOC>
          )}
        </div>
      </div>
    </div>
  );
};
